import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

type MachineClassificationValue = 'complete' | 'partlyCompleted'

function toClassificationValue(
  isPartlyCompletedMachine: boolean | null | undefined
): MachineClassificationValue {
  if (isPartlyCompletedMachine === true) {
    return 'partlyCompleted'
  }
  return 'complete'
}

function toIsPartlyCompletedMachine(
  value: MachineClassificationValue
): boolean {
  return value === 'partlyCompleted'
}

type ProductMetadataMachineClassificationDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  isPartlyCompletedMachine: boolean | null | undefined
  isPending: boolean
  onSave: (isPartlyCompletedMachine: boolean) => void
}

export function ProductMetadataMachineClassificationDialog({
  open,
  onOpenChange,
  isPartlyCompletedMachine,
  isPending,
  onSave,
}: ProductMetadataMachineClassificationDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const currentValue = toClassificationValue(isPartlyCompletedMachine)
  const [selectedValue, setSelectedValue] =
    useState<MachineClassificationValue>(currentValue)

  useEffect(() => {
    if (!open) {
      return
    }
    setSelectedValue(currentValue)
  }, [open, currentValue])

  const hasChange = selectedValue !== currentValue
  const canSave = !isPending && hasChange

  const handleSave = () => {
    if (!canSave) {
      return
    }
    onSave(toIsPartlyCompletedMachine(selectedValue))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='gap-4 sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='text-base'>
            {t('settings.productMetadata.machineClassification.dialog.title')}
          </DialogTitle>
        </DialogHeader>
        <fieldset className='space-y-2'>
          <legend className='text-sm font-medium'>
            {t(
              'settings.productMetadata.machineClassification.dialog.classificationQuestion'
            )}
          </legend>
          <RadioGroup
            value={selectedValue}
            onValueChange={(value) => {
              if (value === 'complete' || value === 'partlyCompleted') {
                setSelectedValue(value)
              }
            }}
            disabled={isPending}
            className='flex flex-col gap-3 pt-1'
          >
            <div className='flex items-center gap-2'>
              <RadioGroupItem value='complete' id='machine-class-complete' />
              <Label
                htmlFor='machine-class-complete'
                className='cursor-pointer font-normal'
              >
                {t(
                  'settings.productMetadata.machineClassification.dialog.completeMachine'
                )}
              </Label>
            </div>
            <div className='flex items-center gap-2'>
              <RadioGroupItem
                value='partlyCompleted'
                id='machine-class-partly-completed'
              />
              <Label
                htmlFor='machine-class-partly-completed'
                className='cursor-pointer font-normal'
              >
                {t(
                  'settings.productMetadata.machineClassification.dialog.partlyCompletedMachinery'
                )}
              </Label>
            </div>
          </RadioGroup>
        </fieldset>
        <Alert variant='warning'>
          <AlertTitle>
            {t(
              'settings.productMetadata.machineClassification.dialog.preservedWorkAlertTitle'
            )}
          </AlertTitle>
          <AlertDescription>
            {t(
              'settings.productMetadata.machineClassification.dialog.preservedWorkAlertDescription'
            )}
          </AlertDescription>
        </Alert>
        <DialogFooter className='gap-2 sm:justify-end'>
          <Button
            type='button'
            variant='outline'
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            {t('settings.productMetadata.machineClassification.dialog.cancel')}
          </Button>
          <Button type='button' disabled={!canSave} onClick={handleSave}>
            {isPending
              ? t(
                  'settings.productMetadata.machineClassification.dialog.saving'
                )
              : t('settings.productMetadata.machineClassification.dialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
